import { useEffect, useSyncExternalStore } from 'react'

/**
 * Which held fields are on screen, and how to open the panel for the rest (GH #1912 option B,
 * docs/ux/2026-10-24-conflict-notice-ux.md, Layout: "Panel off screen").
 *
 * A mounted `EntityConflictBanner` registers as visible for its scope. A panel that can be opened from anywhere (a
 * sidebar tool) registers an opener. The app-level snackbar shows the held fields no visible banner covers, and its
 * Review opens their panel and asks that panel's banner to take focus.
 */

/** The fields a panel edits: a list, or every field except those other panels own */
export type ConflictScope = { fields: readonly string[] } | { exclude: readonly string[] }

/**
 * Held fields edited in a panel of their own (Joni's placements): the poll invite dialog, the playlist's Edit view
 * and the event commands sidebar. The settings sidebar shows every other held field.
 */
export const fieldsWithOwnPanel = ['invited', 'mediaIds', 'eventCommands'] as const

export const inScope = (scope: ConflictScope, field: string) =>
  'fields' in scope ? scope.fields.includes(field) : !scope.exclude.includes(field)

interface ConflictPanel {
  key: string
  entityId: string
  scope: ConflictScope
  /** A banner on screen */
  visible?: boolean
  /** Opens the panel */
  open?: () => void
  /** A banner's "Switched to their version." toast is showing; the off-screen snackbar holds back (GH #2713) */
  toast?: boolean
}

let panels: ConflictPanel[] = []
let focusRequest: { entityId: string; fields: readonly string[] } | null = null
const listeners = new Set<() => void>()
const notify = () => listeners.forEach(listener => listener())

export const conflictPanels = {
  /** Register a panel; returns the unregister */
  register: (panel: ConflictPanel) => {
    panels = [...panels.filter(other => other.key !== panel.key), panel]
    notify()
    return () => {
      panels = panels.filter(other => other !== panel)
      notify()
    }
  },
  /** The opener of a panel on `entityId` whose scope holds any of `fields` */
  openerFor: (entityId: string, fields: readonly string[]) =>
    panels.find(panel => panel.open && panel.entityId === entityId && fields.some(field => inScope(panel.scope, field)))
      ?.open,
  /** Ask the banner that shows these fields, once it is on screen, to take focus */
  requestFocus: (entityId: string, fields: readonly string[]) => {
    focusRequest = { entityId, fields }
    notify()
  },
  subscribe: (listener: () => void) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
  snapshot: () => panels,
}

/** Whether a banner with this scope on `entityId` should take focus now; a request is taken once */
export const takeFocusRequest = (entityId: string, scope: ConflictScope) => {
  if (
    !focusRequest ||
    focusRequest.entityId !== entityId ||
    !focusRequest.fields.some(field => inScope(scope, field))
  ) {
    return false
  }
  focusRequest = null
  return true
}

/** The held fields, per entity, that no visible banner shows */
export const uncoveredConflicts = (conflicts: Record<string, { fields: readonly string[] }>) =>
  Object.entries(conflicts).flatMap(([entityId, { fields }]) => {
    const visible = panels.filter(panel => panel.visible && panel.entityId === entityId)
    const uncovered = fields.filter(field => !visible.some(panel => inScope(panel.scope, field)))
    return uncovered.length > 0 ? [{ entityId, fields: uncovered }] : []
  })

/** For tests */
export const clearConflictPanels = () => {
  panels = []
  focusRequest = null
  notify()
}

/** Re-render when panels register or unregister */
export const useConflictPanels = () =>
  useSyncExternalStore(conflictPanels.subscribe, conflictPanels.snapshot, conflictPanels.snapshot)

/** Register an opener for a panel that can be opened from anywhere, such as a sidebar tool, while mounted */
export const useConflictPanelOpener = ({
  key,
  entityId,
  scope,
  open,
}: {
  key: string
  entityId?: string
  scope: ConflictScope
  open: () => void
}) => {
  const scopeKey = JSON.stringify(scope)
  useEffect(() => {
    if (!entityId) {
      return
    }
    return conflictPanels.register({ key, entityId, scope: JSON.parse(scopeKey) as ConflictScope, open })
  }, [key, entityId, scopeKey, open])
}
